import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Search, Download, Trash2, Folder, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuthContext } from '../App';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';
import { usePageHeader } from '../components/Layout';
import { EmptyState } from '../design/primitives';
import { useWorkspace } from '../contexts/WorkspaceContext';

const API = window.location.origin || 'http://localhost:8001';
axios.defaults.withCredentials = true;

export default function Files() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const debounceRef = useRef(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;
  const safeTotal = total || 0;

  const handleSearchInput = useCallback((value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 400);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFiles();
    fetchProjects();
  }, [page, selectedProject, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedProject]);

  useEffect(() => {
    setSelectedFiles([]);
  }, [selectedProject, searchQuery]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (selectedProject !== 'all') params.project_id = selectedProject;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await axios.get(`${API}/api/files`, { params });
      setFiles(res.data.files || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/api/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const canDelete = () => activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_delete_files')
      );

  const openDelete = (file) => {
    setPendingDelete(file);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/api/files/${pendingDelete.id}`);
      setDeleteOpen(false);
      setPendingDelete(null);
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert('Dosya silinirken bir hata oluştu');
    }
  };

  const handleDownload = (fileId, filename) => {
    const downloadUrl = `${API}/api/files/${fileId}/download?token=${
      localStorage.getItem('session_token') || ''
    }`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || '';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const selectableFiles = useMemo(
    () => (canDelete() ? files : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files, user]
  );
  const selectAll =
    selectableFiles.length > 0 &&
    selectedFiles.length === selectableFiles.length;

  const toggleSelectAll = () => {
    if (selectAll) setSelectedFiles([]);
    else setSelectedFiles(selectableFiles.map((f) => f.id));
  };
  const toggleSelect = (id) => {
    setSelectedFiles((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );
  };

  const doBulkDelete = async () => {
    if (selectedFiles.length === 0) return;
    try {
      await Promise.all(
        selectedFiles.map((fid) => axios.delete(`${API}/api/files/${fid}`))
      );
      setSelectedFiles([]);
      setBulkDeleteOpen(false);
      fetchFiles();
    } catch (err) {
      console.error(err);
      alert('Dosyalar silinirken bir hata oluştu');
    }
  };

  const breadcrumb = useMemo(() => [{ label: 'Dosyalar' }], []);
  const headerActions = useMemo(() => {
    if (selectedFiles.length === 0) return null;
    return (
      <button
        className="ox-btn ox-btn-danger"
        onClick={() => setBulkDeleteOpen(true)}
      >
        <Trash2 size={14} /> {selectedFiles.length} dosyayı sil
      </button>
    );
  }, [selectedFiles.length]);
  usePageHeader(breadcrumb, headerActions, [selectedFiles.length]);

  return (
    <div className="ox-page space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Toplam Dosya" value={safeTotal} />
        <StatTile label="Bu Sayfada" value={files.length} />
        <StatTile
          label="Seçili Proje"
          value={
            selectedProject === 'all'
              ? 'Tümü'
              : projects.find((p) => p.id === selectedProject)?.name || '—'
          }
          isText
        />
        <StatTile label="Seçili Dosya" value={selectedFiles.length} />
      </div>

      {/* Filter bar */}
      <div className="ox-surface p-3 flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]">
            <Search size={14} />
          </span>
          <input
            type="text"
            placeholder="Dosya, görev veya proje ara..."
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            className="ox-input pl-8 w-full"
          />
        </div>
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="ox-select md:max-w-xs"
        >
          <option value="all">Tüm Projeler</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {(searchInput || selectedProject !== 'all') && (
          <button
            className="ox-btn ox-btn-ghost"
            onClick={() => {
              setSearchInput('');
              setSearchQuery('');
              setSelectedProject('all');
            }}
          >
            <X size={14} /> Temizle
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="ox-surface p-10 text-center">
          <div className="text-sm text-[color:var(--text-muted)]">
            Yükleniyor...
          </div>
        </div>
      ) : files.length === 0 ? (
        <div className="ox-surface">
          <EmptyState
            icon={<Folder size={32} />}
            title={searchQuery || selectedProject !== 'all' ? 'Eşleşen dosya bulunamadı' : 'Henüz dosya yok'}
            description={searchQuery || selectedProject !== 'all'
              ? 'Filtreleri temizleyin veya farklı bir arama deneyin.'
              : 'Görevlerinize dosya yüklediğinizde buraya gelecek. Dosyaları görev ayrıntı sayfasından ekleyebilirsiniz.'}
          />
        </div>
      ) : (
        <div className="ox-surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  style={{ borderBottom: '1px solid var(--border)' }}
                  className="ox-surface-muted"
                >
                  {canDelete() && (
                    <th className="px-3 py-2 text-left w-8">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Dosya
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Proje
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Yükleyen
                  </th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Boyut
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)]">
                    Tarih
                  </th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wider text-[color:var(--text-muted)] w-24">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => {
                  const isSelected = selectedFiles.includes(file.id);
                  const { Icon: FileIcon } = getFileIcon(file.filename);
                  const ext = getFileExtension(file.filename);
                  return (
                    <tr
                      key={file.id}
                      className="ox-row-hover"
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      {canDelete() && (
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(file.id)}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2.5 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-md flex items-center justify-center ox-surface-muted flex-shrink-0">
                            <FileIcon size={16} className="ox-text-muted" />
                          </div>
                          <div className="min-w-0">
                            <div
                              className="font-medium truncate"
                              title={file.filename}
                            >
                              {file.filename}
                            </div>
                            <div className="text-[11px] ox-text-muted truncate">
                              {ext && (
                                <span className="ox-mono mr-2">
                                  .{ext.toLowerCase()}
                                </span>
                              )}
                              {file.taskTitle && (
                                <span>Görev: {file.taskTitle}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Folder size={12} className="ox-text-muted" />
                          <span className="truncate">
                            {file.projectName || '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {file.uploaderName || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right ox-mono text-xs">
                        {formatFileSize(file.fileSize)}
                      </td>
                      <td className="px-3 py-2.5 ox-mono text-xs ox-text-muted">
                        {file.createdAt
                          ? new Date(file.createdAt).toLocaleDateString('tr-TR')
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            className="ox-btn ox-btn-ghost"
                            onClick={() =>
                              handleDownload(file.id, file.filename)
                            }
                            title="İndir"
                          >
                            <Download size={14} />
                          </button>
                          {canDelete() && (
                            <button
                              className="ox-btn ox-btn-ghost"
                              style={{ color: 'var(--danger)' }}
                              onClick={() => openDelete(file)}
                              title="Sil"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              className="flex items-center justify-between px-3 py-2.5 ox-surface-muted"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <div className="text-xs ox-text-muted">
                Sayfa{' '}
                <span className="ox-mono">
                  {page}/{totalPages}
                </span>{' '}
                • Toplam <span className="ox-mono">{safeTotal}</span> dosya
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="ox-btn"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={14} /> Önceki
                </button>
                <button
                  className="ox-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Sonraki <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete modal */}
      {deleteOpen && pendingDelete && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setDeleteOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Dosyayı Sil</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                <strong>{pendingDelete.filename}</strong> dosyasını silmek
                istediğinizden emin misiniz?
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setDeleteOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={doDelete}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete modal */}
      {bulkDeleteOpen && (
        <div
          className="ox-modal-backdrop"
          onClick={() => setBulkDeleteOpen(false)}
        >
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <h3>Toplu Silme</h3>
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setBulkDeleteOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="ox-modal-body">
              <p className="text-sm">
                Seçili{' '}
                <strong>{selectedFiles.length}</strong> dosyayı silmek
                istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={() => setBulkDeleteOpen(false)}
              >
                İptal
              </button>
              <button className="ox-btn ox-btn-danger" onClick={doBulkDelete}>
                Tümünü Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, isText }) {
  return (
    <div className="ox-surface p-4">
      <div className="text-[11px] font-medium text-[color:var(--text-muted)] uppercase tracking-wide">
        {label}
      </div>
      <div
        className={`${isText ? 'text-base' : 'text-2xl'} font-semibold ${
          isText ? '' : 'ox-mono'
        } mt-1 truncate`}
      >
        {value}
      </div>
    </div>
  );
}
